import styles from 'css/src/components/common/email-capture/EmailCapturePopup.module.scss';

import React from 'react';

import type { EmailCaptureForm } from 'js/src/components/common/email-capture/email-capture-consts';
import {
    hasDismissedEmailCapture,
    markEmailCaptureDismissed,
} from 'js/src/components/common/email-capture/email-capture-storage';
import HubSpotForm from 'js/src/components/common/email-capture/HubSpotForm';
import Modal, { MODAL_SIZES } from 'js/src/components/common/ui/modal';

const DEFAULT_DELAY_MS = 15000;

type EmailCapturePopupProps = {
    /**
     * Identifies this placement so that a visitor who signs up or dismisses it
     * is not asked again on a later visit.
     */
    placementId: string;
    message: string;
    form: EmailCaptureForm;
    title?: string;
    delayMs?: number;
};

const EmailCapturePopup: React.FC<EmailCapturePopupProps> = React.memo(function EmailCapturePopup({
    placementId,
    message,
    form,
    title = 'Stay in the loop',
    delayMs = DEFAULT_DELAY_MS,
}) {
    const [showing, setShowing] = React.useState(false);

    React.useEffect(() => {
        if (hasDismissedEmailCapture(placementId)) {
            return;
        }
        const timeout = window.setTimeout(() => setShowing(true), delayMs);
        return () => window.clearTimeout(timeout);
    }, [delayMs, placementId]);

    const handleHidden = React.useCallback(() => {
        setShowing(false);
        markEmailCaptureDismissed(placementId);
    }, [placementId]);

    const handleFormSubmitted = React.useCallback(() => {
        markEmailCaptureDismissed(placementId);
    }, [placementId]);

    if (!showing) {
        return null;
    }

    return (
        <Modal
            showing={showing}
            title={title}
            size={MODAL_SIZES.SM}
            className={styles['email-capture-popup']}
            onHidden={handleHidden}
            data-testid="email-capture-popup"
        >
            <p className={styles['email-capture-popup__message']}>{message}</p>
            <HubSpotForm
                className={styles['email-capture-popup__form']}
                portalId={form.portalId}
                formId={form.formId}
                region={form.region}
                onFormSubmitted={handleFormSubmitted}
            />
        </Modal>
    );
});

export default EmailCapturePopup;
